import api from '../utils/api';

/**
 * The canvas is the DRAFT of a scenario's graph, and this is the whole client
 * surface for it. Editing goes through exactly one write path — `sendOps` — so
 * there is no longer a per-entity endpoint per gesture to keep in sync.
 *
 * The dual-write endpoints this file used to wrap (situation-node, choice-edge,
 * situation-node-edge, node position, copy, load) no longer exist on either side.
 */

const BASE = '/api/canvas';

/**
 * Fetch a scenario's draft canvas. Owners and collaborators only — the draft holds
 * unpublished text, hints and answers, so a reader who cannot edit the scenario
 * gets a 403 and should read `getPublishedMap` instead.
 * @route GET /api/canvas/:scenarioId
 * @param {string} scenarioId
 * @returns {Promise<Object>} The canvas, including `version` and `publishedVersion`
 * @throws 403 when the caller is not the creator or a collaborator
 */
export const getCanvas = (scenarioId) =>
    api.get(`${BASE}/${scenarioId}`).then((r) => r.data);

/**
 * Fetch the public graph skeleton as of the last publish — ids, positions,
 * titles and endpoint types only. This is what the scenario viewer's minimap
 * reads; it never exposes the draft.
 * @route GET /api/canvas/:scenarioId/published-map
 * @param {string} scenarioId
 * @returns {Promise<{nodes: Array, edges: Array}>}
 */
export const getPublishedMap = (scenarioId) =>
    api.get(`${BASE}/${scenarioId}/published-map`).then((r) => r.data);

/**
 * Create an empty canvas for a scenario.
 * @route POST /api/canvas/:scenarioId
 * @param {string} scenarioId
 * @returns {Promise<Object>} The canvas (existing or newly created)
 */
export const createCanvas = (scenarioId) =>
    api.post(`${BASE}/${scenarioId}`).then((r) => r.data);

/**
 * Apply a batch of draft-editing ops — the single write path for the editor.
 *
 * Creates carry client-generated ids, so a retried batch is idempotent rather
 * than duplicating work. `baseVersion` is optional; when supplied and stale the
 * server responds 409 with `currentVersion` so the caller can refetch and replay.
 *
 * An accepted batch is rebroadcast to the scenario's other editors as
 * `canvasOpsApplied`; `clientId` comes back with it so this tab can drop its own
 * echo (see utils/clientId.js).
 *
 * @route PATCH /api/canvas/:scenarioId/ops
 * @param {string} scenarioId
 * @param {Array<Object>} ops
 * @param {{baseVersion?: number, clientId?: string}} [options]
 * @returns {Promise<{version: number, publishedVersion: number}>}
 */
export const sendOps = (scenarioId, ops, { baseVersion, clientId } = {}) => {
    const body = { ops };
    if (baseVersion !== undefined) body.baseVersion = baseVersion;
    if (clientId) body.clientId = clientId;

    return api.patch(`${BASE}/${scenarioId}/ops`, body).then((r) => r.data);
};

/**
 * Publish the draft: validate it, then materialize it into the Situation/Choice
 * documents players read.
 *
 * A successful publish is broadcast to the scenario's other editors as
 * `canvasPublished`; `clientId` comes back with it so this tab drops its own echo,
 * exactly as `sendOps` does (see utils/clientId.js).
 *
 * @route POST /api/canvas/:scenarioId/publish
 * @param {string} scenarioId
 * @param {{clientId?: string}} [options]
 * @returns {Promise<{scenario: Object, publishedVersion: number}>}
 * @throws On 422, `error.response.data.errors` is shaped like `checkValidity`'s output
 */
export const publishCanvas = (scenarioId, { clientId } = {}) =>
    api.post(`${BASE}/${scenarioId}/publish`, clientId ? { clientId } : {}).then((r) => r.data);

/**
 * Ask the server whether the draft can be published, without publishing it.
 * @route GET /api/canvas/:scenarioId/validate
 * @param {string} scenarioId
 * @returns {Promise<{valid: boolean, errors: Array}>}
 */
export const validateCanvas = (scenarioId) =>
    api.get(`${BASE}/${scenarioId}/validate`).then((r) => r.data);

/**
 * Upload a media asset and get its URL back. The caller then sends an ordinary
 * updateNodeData/updateEdgeData op carrying that URL, so a failed upload can
 * never leave a half-applied graph edit behind.
 *
 * @route POST /api/canvas/:scenarioId/media
 * @param {string} scenarioId
 * @param {{kind: 'image'|'audio'|'document', file: File, replacesUrl?: string}} params
 * @returns {Promise<{url: string}>}
 */
export const uploadMedia = (scenarioId, { kind, file, replacesUrl }) => {
    const form = new FormData();
    form.append('kind', kind);
    form.append(kind, file);
    if (replacesUrl) form.append('replacesUrl', replacesUrl);

    return api
        .post(`${BASE}/${scenarioId}/media`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
};

/**
 * Fetch the usage heat map (per-node/edge unique visitor counts as colors, plus
 * the contributing users).
 * @route GET /api/canvas/heatMap/:scenarioId
 * @param {string} scenarioId
 * @returns {Promise<{nodes: Array, edges: Array, nodeUsers: Array, edgeUsers: Array}>}
 */
export const getHeatmap = (scenarioId) =>
    api.get(`${BASE}/heatMap/${scenarioId}`).then((r) => r.data);
